<script setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { useAuthStore } from './stores/auth.js'
import { useOrgStore } from './stores/orgStore.js'
import { usePermissions } from './utils/permissions.js'
import MobileMenu from './components/MobileMenu.vue'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const orgStore = useOrgStore()
const { can } = usePermissions(authStore, orgStore)

const showNav = computed(() => {
  if (!authStore.isAuthenticated) return false
  return route.path !== '/login' && route.path !== '/change-password' && route.path !== '/no-access'
})

const isMobileMenuOpen = ref(false)
const openMobileMenu = () => { isMobileMenuOpen.value = true }
const closeMobileMenu = () => { isMobileMenuOpen.value = false }

const canManageUsers = computed(() => can.manageUsers.value)
const userDisplayName = computed(() => {
  const fullName = String(authStore.user?.full_name ?? '').trim()
  if (fullName) return fullName
  const username = String(authStore.user?.username ?? '').trim()
  return username || 'Account'
})

const activeOrgIdModel = computed({
  get: () => (orgStore.activeOrgId === null ? '' : String(orgStore.activeOrgId)),
  set: (value) => {
    if (!value) { orgStore.setActiveOrg(null); return }
    orgStore.setActiveOrg(Number(value))
  },
})

const activeSubOrgIdModel = computed({
  get: () => (orgStore.activeSubOrgId === null ? '' : String(orgStore.activeSubOrgId)),
  set: (value) => {
    if (!value) { orgStore.setActiveSubOrg(null); return }
    orgStore.setActiveSubOrg(Number(value))
  },
})

const onMobileOrgUpdate = (value) => { activeOrgIdModel.value = value === null ? '' : String(value) }
const onMobileSubOrgUpdate = (value) => { activeSubOrgIdModel.value = value === null ? '' : String(value) }

const logout = async () => {
  authStore.logout()
  await router.push('/login')
}

const showTopNav = ref(true)

let lastScrollY = 0

const handleScroll = () => {
  const currentScrollY = window.scrollY

  // alltid vis nav helt øverst
  if (currentScrollY < 10) {
    showTopNav.value = true
    lastScrollY = currentScrollY
    return
  }

  // skjul ved scrolling ned
  if (currentScrollY > lastScrollY && currentScrollY > 80) {
    showTopNav.value = false
  }

  // vis ved scrolling opp
  if (currentScrollY < lastScrollY) {
    showTopNav.value = true
  }

  lastScrollY = currentScrollY
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
})

</script>

<template>
  <div>
    <header v-if="showNav" :class="['top-nav', { hidden: !showTopNav }]">
      <div class="left">
        <img class="icon-logo" src="/web-app-manifest-512x512.png" alt="Logo" />

        <!-- Desktop nav links (hidden on mobile via CSS) -->
        <nav class="links desktop-only">
          <RouterLink to="/dashboard">Dashboard</RouterLink>
          <RouterLink v-if="can.viewLotteries.value" to="/lotteries">Lotteries</RouterLink>
          <RouterLink v-if="can.viewBatches.value" to="/seller">Seller</RouterLink>
          <RouterLink v-if="canManageUsers" to="/users">Users</RouterLink>
          <RouterLink v-if="authStore.isSuperAdmin" to="/admin/orgs">Organizations</RouterLink>
          <RouterLink v-if="authStore.isAdmin" to="/admin/suborgs">SubOrgs</RouterLink>
          <RouterLink v-if="authStore.isSuperAdmin" to="/admin/smtp">SMTP</RouterLink>
          <RouterLink v-if="authStore.isSuperAdmin" to="/admin/audit-logs">Audit Logs</RouterLink>
          <RouterLink v-if="authStore.isSuperAdmin" to="/admin/operations">Operations</RouterLink>
          <RouterLink v-if="authStore.isSuperAdmin" to="/admin/financial-overview">Financial Overview</RouterLink>
<!--          <RouterLink v-if="authStore.isSuperAdmin || canManageUsers" to="/admin/financial-overview">Financial Overview</RouterLink>-->
        </nav>
      </div>

      <div class="right-controls">
        <!-- Org pickers: visible on desktop, hidden on mobile (accessible via MobileMenu) -->
        <div class="org-controls desktop-only">
          <label for="org-select">Organization</label>
          <select id="org-select" v-model="activeOrgIdModel" :disabled="orgStore.loading || !orgStore.availableOrganizations.length">
            <option value="">Select organization</option>
            <option v-for="org in orgStore.availableOrganizations" :key="org.id" :value="String(org.id)">
              {{ org.name }}
            </option>
          </select>
          <label for="suborg-select">Sub-organization</label>
          <select
            id="suborg-select"
            v-model="activeSubOrgIdModel"
            :disabled="!orgStore.activeOrgId || !orgStore.filteredSubOrgs.length"
          >
            <option value="">Select sub-organization</option>
            <option v-for="subOrg in orgStore.filteredSubOrgs" :key="subOrg.id" :value="String(subOrg.id)">
              {{ subOrg.name }}
            </option>
          </select>
          <span v-if="orgStore.activeOrgRole" class="role-badge">{{ orgStore.activeOrgRole }}</span>
          <span v-if="orgStore.loading" class="org-status">Loading...</span>
          <span v-else-if="orgStore.error" class="org-error">{{ orgStore.error }}</span>
        </div>

        <!-- User menu (desktop) -->
        <details class="user-menu desktop-only">
          <summary>{{ userDisplayName }}</summary>
          <div class="user-menu-items">
            <RouterLink to="/settings/profile">Profile</RouterLink>
            <RouterLink to="/settings/security">Security</RouterLink>
            <button type="button" @click="logout">Logout</button>
          </div>
        </details>

        <!-- Hamburger button: visible only on mobile via CSS -->
        <button
          class="hamburger-btn mobile-only"
          type="button"
          aria-label="Open menu"
          :aria-expanded="isMobileMenuOpen"
          @click="openMobileMenu"
        >
          <span class="hamburger-line" />
          <span class="hamburger-line" />
          <span class="hamburger-line" />
        </button>
      </div>
    </header>

    <!-- Mobile slide-in menu -->
    <MobileMenu
      :is-open="isMobileMenuOpen"
      :can-manage-users="canManageUsers"
      :can-view-batches="can.viewBatches.value"
      :is-super-admin="authStore.isSuperAdmin"
      :user-display-name="userDisplayName"
      :active-org-id="orgStore.activeOrgId"
      :active-sub-org-id="orgStore.activeSubOrgId"
      :organizations="orgStore.availableOrganizations"
      :sub-orgs="orgStore.subOrgs ?? []"
      @logout="logout"
      @close="closeMobileMenu"
      @update:active-org-id="onMobileOrgUpdate"
      @update:active-sub-org-id="onMobileSubOrgUpdate"
    />

    <RouterView />
  </div>
</template>

<style>
/* ── Variables & resets already in base.css ── */

body {
  margin: 0;
  padding: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
}

/* ── User menu ── */
.user-menu {
  position: relative;
}

.user-menu summary {
  list-style: none;
  cursor: pointer;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  background: #fff;
  color: #111827;
  font-weight: 600;
  min-height: 44px;
  display: flex;
  align-items: center;
}

.user-menu summary::-webkit-details-marker { display: none; }

.user-menu-items {
  position: absolute;
  right: 0;
  top: calc(100% + 0.4rem);
  min-width: 160px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(17, 24, 39, 0.12);
  padding: 0.4rem;
  display: grid;
  gap: 0.25rem;
  z-index: 20;
}

.user-menu-items a,
.user-menu-items button {
  text-align: left;
  width: 100%;
  text-decoration: none;
  color: #111827;
  border: none;
  border-radius: 6px;
  padding: 0.45rem 0.5rem;
  background: transparent;
  cursor: pointer;
  font-weight: 500;
  min-height: 44px;
  display: flex;
  align-items: center;
}

.user-menu-items a:hover,
.user-menu-items button:hover { background: #f3f4f6; }

.user-menu-items button { color: #b91c1c; }

/* ── Org / role bits ── */
.role-badge {
  padding: 0.2rem 0.45rem;
  border-radius: 9999px;
  background: #dbeafe;
  color: #1e40af;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}

.org-status { font-size: 0.75rem; color: #6b7280; }

.org-error {
  font-size: 0.75rem;
  color: #b91c1c;
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Desktop nav link colours (layout in layout.css) ── */
.links a {
  text-decoration: none;
  color: #1d4ed8;
  font-weight: 600;
  padding: 0 0.25rem;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
}

.links a.router-link-active { color: #111827; }

/* ── Top-nav button baseline ── */
.top-nav button {
  border: none;
  border-radius: 6px;
  background: #dc2626;
  color: #fff;
  padding: 0.45rem 0.8rem;
}

/* ── Hamburger button ── */
.mobile-only {
  display: none;
}

.desktop-only {
  display: flex;
}

.hamburger-btn {
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 44px;
  height: 44px;
  padding: 0;
  background: transparent;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  color: inherit;
  min-height: unset;
}

.hamburger-btn:hover { background: #f3f4f6; }

.hamburger-line {
  display: block;
  width: 20px;
  height: 2px;
  background: #111827;
  border-radius: 2px;
}

@media (max-width: 768px), (max-height: 500px){
  .mobile-only {
    display: flex;
  }

  .desktop-only {
    display: none;
  }

  .hamburger-btn {
    display: flex;
  }
}
</style>
